import {CommunicationMediaDTO} from '../../communication';

export type FundraiserStatus = 'Created' | 'Published' | 'AmountCollected' | 'Completed' | 'Canceled';

// Единственный источник перечислений сортировки Сборов — остальные потребители переэкспортируют или импортируют отсюда.
// Списки объявлены значениями, а не только типами: значения сортировки приходят строкой из URL, и без
// проверки по списку в рантайме произвольная строка дошла бы до бэкенда, который отвечает на неё 400.
export const SORT_PROPERTIES = ['name', 'createdAt', 'amountCollectedAt', 'lastMediaUploadAt'] as const;
export type SortProperty = (typeof SORT_PROPERTIES)[number];

export const SORT_ORDERS = ['ascending', 'descending'] as const;
export type SortOrder = (typeof SORT_ORDERS)[number];

const parseSortProperty = (value: string | null | undefined): SortProperty | undefined =>
  SORT_PROPERTIES.includes(value as SortProperty) ? (value as SortProperty) : undefined;

const parseSortOrder = (value: string | null | undefined): SortOrder | undefined =>
  SORT_ORDERS.includes(value as SortOrder) ? (value as SortOrder) : undefined;

export interface SortParams {
  sortProperty?: SortProperty;
  sortOrder?: SortOrder;
}

// Свойство и направление разбираем парой, а не по отдельности: в URL они могут разъехаться (правка адреса
// руками), и тогда таблица подсветила бы одно направление, а бэкенд при отсутствующем `sortOrder` применил бы
// своё значение по умолчанию — порядок на экране разошёлся бы с подсказкой в заголовке. Без валидного свойства
// направление бессмысленно и отбрасывается вместе с ним; пропущенное или неизвестное направление при валидном
// свойстве приводим к `ascending` — тому же значению, которое таблица показывает по умолчанию.
export const parseSortParams = (searchParams: URLSearchParams): SortParams => {
  const sortProperty = parseSortProperty(searchParams.get('sortProperty'));

  if (!sortProperty) {
    return {};
  }

  return {sortProperty, sortOrder: parseSortOrder(searchParams.get('sortOrder')) ?? 'ascending'};
};

export interface FundraiserModel {
  id: string;
  name: string;
  status: FundraiserStatus;
  isUrgent: boolean;
  createdAt: string;
  amountCollectedAt: string | null;
  requiredAmount: number;
  externalId: string | null;
  fund: {
    id: string;
    name: string;
  };
  region: {
    id: string;
    name: string;
  };
  categories: {
    id: string;
    name: string;
  }[];
  feedImageUrl: string | null;
  // Необязательное: поле появляется в ответе только после выката бэкенда, а FundraiserModel
  // переиспользуют getFundraiserById/createFundraiser/updateFundraiser, которые его не возвращают
  lastMediaUploadAt?: string | null;
}

export interface FundraisersResponse {
  items: FundraiserModel[];
  totalCount: number;
  pageInfo: PageInfoModel;
}

export interface FundraisersFilter {
  query?: string;
  sortProperty?: SortProperty;
  sortOrder?: SortOrder;
  categories?: string[];
  regions?: string[];
  funds?: string[];
  statuses?: string[];
  pageSize?: number;
  after?: string;
}

export interface CreateFundraiserDTO {
  general: {
    name: string;
    shortName: string | null;
    shortDescription: string;
    detailedDescription: string;
    fundId: string;
    regionId: string;
    categoryIds: string[];
    externalId: string | null;
  };
  financialAndTiming: {
    requiredAmount: number | null;
    commissionPercentage: number;
    end: Date | null;
  };
  isUrgent: boolean;
  feedImageUploadId: string;
}

export interface FundraiserDetailedModel {
  id: string;
  name: string;
  shortName?: string | null;
  shortDescription?: string;
  detailedDescription?: string;
  isUrgent?: boolean;
  status: FundraiserStatus;
  createdAt: string;
  updatedAt: string;
  publishedAt: string | null;
  amountCollectedAt: string | null;
  completedAt: string | null;
  cancelledAt: string | null;
  externalId: string | null;
  fund: {
    id: string;
    name: string;
  };
  region: {
    id: string;
    name: string;
  };
  categories: {
    id: string;
    name: string;
  }[];
}

export interface FundraiserFinanceModel {
  id: string;
  requiredAmount: number | null;
  commissionPercentage: number;
  end: string | null;
  amountCollectedAt: string | null;
  collectedAmount: number;
}

export interface UpdateFundraiserDTO {
  general: {
    name: string;
    shortName: string | null;
    shortDescription: string;
    detailedDescription: string;
    fundId: string;
    regionId: string;
    categoryIds: string[];
    externalId: string | null;
  };
}

export interface UpdateFundraiserParams {
  fundraiserId: string;
  data: UpdateFundraiserDTO;
}

export interface UpdateFundraiserFinanceDTO {
  requiredAmount: number | null;
  commissionPercentage: number;
  end: string | null;
}

export interface UpdateFundraiserFinanceParams {
  fundraiserId: string;
  data: UpdateFundraiserFinanceDTO;
}

// Типы для трансфера средств от бенефактора
export interface BenefactorTransferDTO {
  benefactorId: string;
  amount: number;
  serviceFee: number;
  bookkeepingNumber?: string | null;
  note?: string | null;
}

export interface BenefactorTransferFormData {
  benefactorId: string;
  amount: string;
  serviceFee: string;
  bookkeepingNumber: string;
  note: string;
}

export interface ProjectTransferDTO {
  amount: number;
}

export interface ProjectTransferFormData {
  amount: string;
}

// Типы для историй сборов
export interface FundraiserStoryModel {
  id: string;
  date: string;
  title: string;
  priority: number;
  publishedAt: string | null;
  previewImage: string | null;
}

export interface FundraiserStoriesResponse {
  items: FundraiserStoryModel[];
  pageInfo: {
    hasNextPage: boolean;
    hasPreviousPage: boolean;
    startCursor: string | null;
    endCursor: string | null;
  };
  totalCount: number;
}

export interface CreateFundraiserStoryDTO {
  date: string;
  title: string;
  previewImageUploadId: string;
}

export interface UpdateFundraiserStoryDTO {
  date: string;
  title: string;
  priority: number;
  previewImageUploadId?: string;
}

export interface FundraiserStoriesFilter {
  appealId: string;
  after?: string;
  pageSize?: number;
}

// Детальная история сбора с экранами
export interface FundraiserStoryDetailedModel {
  id: string;
  date: string;
  title: string;
  priority: number;
  publishedAt: string | null;
  screens: FundraiserStoryScreenModel[];
  previewImage: string | null;
}

export interface FundraiserStoryMediaFileModel {
  fileName: string;
  // Базовое изображение (base@jpg) или файл видео
  url: string;
  // Превью экрана (story@webp) у изображений и постеров; у файла видео отсутствует
  thumbnailUrl?: string | null;
}

export interface FundraiserStoryMediaImageModel {
  type: 'image';
  image: FundraiserStoryMediaFileModel;
}

export interface FundraiserStoryMediaVideoModel {
  type: 'video';
  video: FundraiserStoryMediaFileModel;
  thumbnail?: FundraiserStoryMediaFileModel;
}

export type FundraiserStoryMediaModel = FundraiserStoryMediaImageModel | FundraiserStoryMediaVideoModel;

export interface FundraiserStoryScreenModel {
  media?: FundraiserStoryMediaModel | null;
  paragraphs: FundraiserStoryParagraphModel[];
  alignToBottom: boolean;
  button: FundraiserStoryButtonModel | null;
  theme: 'Light' | 'Dark';
}

export interface FundraiserStoryParagraphModel {
  style: string;
  text: string;
}

export interface FundraiserStoryButtonModel {
  text: string;
  link: string;
  theme: 'Default' | 'White' | 'Black';
}

export interface FundraiserDocumentModel {
  id: string;
  fileName: string;
  title: string;
  url: string;
  createdAt: string;
}

export interface FundraiserDocumentsResponse {
  items: FundraiserDocumentModel[];
  pageInfo: {
    hasNextPage: boolean;
    hasPreviousPage: boolean;
    startCursor: string | null;
    endCursor: string | null;
  };
  totalCount: number;
}

export interface FundraiserDocumentsFilter {
  pageSize?: number;
  after?: string;
}

export interface CreateFundraiserNotificationDTO {
  subject: string;
  body: string;
  group: string;
  button: {
    text: string;
    link: string;
  } | null;
  media?: CommunicationMediaDTO | null;
}
